import React, { useState } from "react";
import { useAuth } from "../../game/auth";
import { Skull, User as UserIcon, Chrome } from "lucide-react";

const BG = "https://images.pexels.com/photos/4012056/pexels-photo-4012056.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export const LoginScreen = () => {
  const { login, register, loginWithGoogle, enterAsGuest, loading } = useAuth();
  const [mode, setMode] = useState("login"); // login | register
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === "login") await login(username, password);
      else await register(username, password);
    } catch (ex) {
      const d = ex?.response?.data?.detail;
      const msg = typeof d === "string" ? d : (Array.isArray(d) ? d.map((x) => x.msg).join(" ") : (ex.message || "Something went wrong."));
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="relative min-h-screen w-full flex items-center justify-center" data-testid="auth-loading">
        <p className="font-mono text-xs tracking-[0.4em] uppercase text-white/40">// checking session ...</p>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen w-full overflow-hidden" data-testid="login-screen">
      <div className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${BG})`, filter: "brightness(0.28) saturate(0.5) contrast(1.2)" }} aria-hidden />
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-transparent" aria-hidden />
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-transparent to-black/95" aria-hidden />

      <div className="relative z-10 min-h-screen grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="flex flex-col justify-between px-8 md:px-16 lg:px-24 py-12">
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-emergency animate-heartbeat" aria-hidden />
            <span className="font-mono text-xs tracking-[0.4em] uppercase text-white/50">
              Orpheus Biomedical // Blackwood Annex
            </span>
          </div>

          <div className="max-w-md">
            <p className="font-mono text-xs tracking-[0.35em] uppercase text-white/40 mb-3">
              // {mode === "login" ? "SIGN IN" : "REGISTER"}
            </p>
            <h1 className="horror-title text-5xl md:text-6xl leading-[0.95] mb-3">
              <span className="text-white/85">Enter</span> <span className="accent">the</span> <span className="text-white/85">facility</span>
            </h1>
            <p className="narrative text-white/55 text-sm md:text-base mb-8">
              {mode === "login"
                ? "Your file is on the desk where you left it."
                : "Every new resident is assigned a number. Yours will follow you home."}
            </p>

            <form onSubmit={submit} className="flex flex-col gap-4">
              <label className="flex flex-col gap-2">
                <span className="font-mono text-[10px] tracking-[0.35em] uppercase text-white/45">USERNAME</span>
                <input
                  type="text"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  data-testid="auth-username"
                  required
                  minLength={3}
                  className="bg-black/80 border border-white/15 focus:border-emergency outline-none px-4 py-3 text-white font-mono tracking-wider transition-colors"
                />
              </label>
              <label className="flex flex-col gap-2">
                <span className="font-mono text-[10px] tracking-[0.35em] uppercase text-white/45">PASSWORD</span>
                <input
                  type="password"
                  autoComplete={mode === "login" ? "current-password" : "new-password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="auth-password"
                  required
                  minLength={6}
                  className="bg-black/80 border border-white/15 focus:border-emergency outline-none px-4 py-3 text-white font-mono tracking-wider transition-colors"
                />
              </label>

              {error && (
                <div className="border border-emergency/50 bg-blood/20 px-4 py-2 font-mono text-xs text-emergency" data-testid="auth-error">
                  // {error}
                </div>
              )}

              <button type="submit" disabled={busy} className="term-btn term-btn-danger" data-testid="auth-submit">
                <span className="inline-flex items-center gap-3">
                  <UserIcon size={14} />
                  {busy ? (mode === "login" ? "SIGNING IN..." : "CREATING FILE...") : (mode === "login" ? "SIGN IN" : "CREATE ACCOUNT")}
                </span>
              </button>
            </form>

            <div className="my-6 flex items-center gap-4">
              <span className="flex-1 h-px bg-white/10" />
              <span className="font-mono text-[10px] tracking-[0.35em] uppercase text-white/30">OR</span>
              <span className="flex-1 h-px bg-white/10" />
            </div>

            <button onClick={loginWithGoogle} className="term-btn w-full" data-testid="auth-google">
              <span className="inline-flex items-center gap-3">
                <Chrome size={14} />
                CONTINUE WITH GOOGLE
              </span>
            </button>

            <button onClick={enterAsGuest} className="term-btn w-full mt-3" data-testid="auth-guest">
              <span className="inline-flex items-center gap-3">
                <Skull size={14} />
                PLAY AS GUEST — progress won&apos;t sync across devices
              </span>
            </button>

            <div className="mt-8 text-center">
              <button
                onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(null); }}
                className="font-mono text-[11px] tracking-[0.3em] uppercase text-white/45 hover:text-emergency transition-colors"
                data-testid="auth-toggle-mode"
              >
                {mode === "login" ? "// need a file? register" : "// already have one? sign in"}
              </button>
            </div>
          </div>

          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/25">
            headphones recommended // lights off
          </p>
        </div>

        <div className="hidden lg:flex items-center justify-center relative">
          <div className="w-[2px] h-2/3 bg-gradient-to-b from-transparent via-emergency/40 to-transparent" />
        </div>
      </div>
    </div>
  );
};
